import React from 'react';
import { exportCallDataToExcel } from '../../utils/excelExport';

interface ConsolidatedCallData {
    userId: string;
    userName: string;
    totalOutboundCalls: number;
    totalInboundCalls: number;
    totalCalls: number;
    connectedCalls: number;
    missedCalls: number;
    uniqueCalls: number;
    totalDuration: number;
    outboundDuration: number;
    inboundDuration: number;
    averageCallDurationTotal: number;
    averageCallDurationConnected: number;
    connectionRate: string;
    callAttemptTarget: number;
    targetAchievement: string;
}

interface ExcelPreviewModalProps {
    isOpen: boolean;
    onClose: () => void;
    data: ConsolidatedCallData[];
    fileName: string;
    onDownloadSuccess: (message: string) => void;
    onDownloadError: (message: string) => void;
}

const ExcelPreviewModal: React.FC<ExcelPreviewModalProps> = ({
    isOpen,
    onClose,
    data,
    fileName,
    onDownloadSuccess,
    onDownloadError
}) => {
    const [isDownloading, setIsDownloading] = React.useState(false);

    // Helper function to format duration from seconds to HH:MM:SS
    const formatDuration = (seconds: number): string => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = seconds % 60;

        if (hours > 0) {
            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
        } else {
            return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
        }
    };

    const handleDownload = async () => {
        setIsDownloading(true);
        try {
            const result = exportCallDataToExcel(data, fileName);
            
            if (result.success) {
                onDownloadSuccess(`Successfully exported consolidated call data for ${result.recordCount} staff members to ${result.fileName}`);
                onClose();
            } else {
                onDownloadError(`Export failed: ${result.error}`);
            }
        } catch (error) {
            console.error('Error downloading Excel:', error);
            onDownloadError('Failed to export call data. Please try again.');
        } finally {
            setIsDownloading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-3xl p-6 max-w-7xl w-full mx-4 max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h2 className="text-2xl font-semibold text-gray-900">Excel Export Preview</h2>
                        <p className="text-sm text-gray-600 mt-1">
                            Preview of call data for {data.length} staff members
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        disabled={isDownloading}
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Table Container */}
                <div className="flex-1 overflow-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">S.No</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User Name</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Outbound</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Inbound</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Calls</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Connected</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Missed</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unique Calls</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Connection Rate</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Duration</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Outbound Duration</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Inbound Duration</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Duration - All</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Duration - Connected</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Call Target</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Target Achievement</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {data.map((row, index) => (
                                <tr key={row.userId} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{index + 1}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{row.userName}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.totalOutboundCalls}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.totalInboundCalls}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.totalCalls}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.connectedCalls}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.missedCalls}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-blue-600">{row.uniqueCalls}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.connectionRate}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{formatDuration(row.totalDuration)}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{formatDuration(row.outboundDuration)}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{formatDuration(row.inboundDuration)}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{formatDuration(row.averageCallDurationTotal)}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{formatDuration(row.averageCallDurationConnected)}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.callAttemptTarget}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.targetAchievement}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Footer */}
                <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200">
                    <div className="text-sm text-gray-600">
                        <span className="font-medium">{data.length}</span> staff members • 
                        <span className="font-medium"> {data.reduce((sum, row) => sum + row.totalCalls, 0)}</span> total calls • 
                        <span className="font-medium"> {data.reduce((sum, row) => sum + row.uniqueCalls, 0)}</span> unique calls
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            disabled={isDownloading}
                            className="px-6 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleDownload}
                            disabled={isDownloading}
                            className="px-6 py-2 text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {isDownloading ? (
                                <>
                                    <svg className="animate-spin h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Downloading...
                                </>
                            ) : (
                                <>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    Download Excel
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExcelPreviewModal;